import { HttpErrorResponse } from '@angular/common/http';
import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import {
  catchError,
  EmptyError,
  forkJoin,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  tap,
  throwError,
  throwIfEmpty,
  timeout,
  TimeoutError,
} from 'rxjs';
import {
  AuthService,
  CaseCompositionService,
  ComposedCase,
  DossierService,
  EngineInboxService,
  httpStatusOf,
  TelemetryEventProperties,
  TelemetryService,
  ToasterService,
  WORKFLOW_ENGINE_GATEWAY,
} from '#services';
import { TelemetryEvent } from '#enums';
import { ENGINE_READ_TIMEOUT_MS } from '../../../../services/workflow-engine/http-workflow-engine.gateway';
import { EngineCaseNotFoundError } from '../../../../services/workflow-engine/case-composition.service';
import { WorkflowLoadFeedbackService } from '../../workflow-load-feedback.service';
import { HeldRole } from '../../../../services/workflow-engine/generated/models/held-role';
import { DecideHttpRequest } from '../../../../services/workflow-engine/generated/models/decide-http-request';
import { StepForm } from '../../../../services/workflow-engine/generated/models/step-form';
import {
  buildFieldViews,
  buildGraphView,
  buildDecideBody,
  describeCommandError,
  heldRoles,
  outcomeNeedsReason,
  RETURNED_OUTCOME,
  type FormFieldView,
} from './engine-case.mapper';

type CaseCommand = 'claim' | 'decide' | 'release' | 'resolve_suggestion';

/** The event a command reports when the engine accepts it. */
interface CommandSuccess {
  event: TelemetryEvent;
  properties?: TelemetryEventProperties;
}

function decisionSuccess(outcome: string | null): CommandSuccess {
  return outcome === RETURNED_OUTCOME
    ? { event: TelemetryEvent.CaseReturned }
    : { event: TelemetryEvent.CaseDecisionSubmitted, properties: { outcome } };
}

/** A claim with no answer may or may not have been applied, so it is `unconfirmed` rather than `failed`. */
function claimFailureOutcome(error: unknown): string {
  if (error instanceof TimeoutError || error instanceof EmptyError || (error instanceof HttpErrorResponse && error.status === 0)) {
    return 'unconfirmed';
  }
  const status = httpStatusOf(error);
  return status === 409 ? 'conflict' : status === 403 ? 'denied' : 'failed';
}

/** When a case read failed: on opening, or on the re-read after a command. */
type LoadAttempt = 'initial' | 'after_command';

/** Shown when the case cannot be read for a reason other than the viewer lacking access. */
export const CASE_LOAD_FAILED_MESSAGE = 'The workflow case could not be loaded. Reopen the case to try again.';

/** A read refused for lack of access, or for a case that does not exist, is expected and not reported as broken. */
function isExpectedReadRefusal(error: unknown): boolean {
  const status = httpStatusOf(error);
  return status === 403 || status === 404;
}

/** The longest a command may wait for its answer before the screen re-reads the case to show what the engine holds. */
export const ENGINE_COMMAND_TIMEOUT_MS = 30_000;

/** Recorded on the step's audit trail when a decision sent with an automatic claim fails. */
export const AUTO_RELEASE_REASON = 'Released automatically: the decision sent with this claim could not be completed.';

/** One open engine case: reads, the viewer's role grants, and the claim / decide / release commands with a re-read after each. */
@Injectable()
export class EngineCaseStore {
  private _gateway = inject(WORKFLOW_ENGINE_GATEWAY);
  private _composition = inject(CaseCompositionService);
  private _dossiers = inject(DossierService);
  private _auth = inject(AuthService);
  private _telemetry = inject(TelemetryService);
  private _toaster = inject(ToasterService);
  private _inbox = inject(EngineInboxService);
  private _loadFeedback = inject(WorkflowLoadFeedbackService, { optional: true });

  private _caseId = signal<string | null>(null);
  private _composed = signal<ComposedCase | null>(null);
  private _form = signal<StepForm | null>(null);
  private _roles = signal<HeldRole[]>([]);
  private _loading = signal(false);
  private _busy = signal(false);
  private _loadFailed = signal(false);
  private _signInRedirect = signal(false);
  private _caseMissing = signal(false);
  private _loadStatus = signal<number | null>(null);
  private _commandError = signal<string | null>(null);
  // Only one case is open at a time; switching cases cancels whatever the previous case still had in flight.
  private _inFlight: Subscription | null = null;
  // Every command re-reads the case, so the step form is reported once per case, step, and class version.
  private _lastLoadedForm: string | null = null;

  composed = this._composed.asReadonly();
  form = this._form.asReadonly();
  loading = this._loading.asReadonly();
  busy = this._busy.asReadonly();
  loadFailed = this._loadFailed.asReadonly();
  signInRedirect = this._signInRedirect.asReadonly();
  caseMissing = this._caseMissing.asReadonly();
  loadStatus = this._loadStatus.asReadonly();
  commandError = this._commandError.asReadonly();

  graph = computed(() => {
    const value = this._composed();
    return value ? buildGraphView(value.graph, value.state, value.audit) : [];
  });
  fields = computed<FormFieldView[]>(() => {
    const form = this._form();
    return form ? buildFieldViews(form) : [];
  });
  activeStep = computed(() => this._composed()?.state.activeStep ?? null);
  isOpen = computed(() => this._composed()?.state.status === 'open');
  stepRole = computed(() => this._form()?.role ?? null);
  holdsStepRole = computed(() => {
    const role = this.stepRole();
    return role !== null && this._roles().some(held => held.role === role);
  });
  isOwner = computed(() => {
    const owner = this._composed()?.state.ownerOid ?? null;
    return owner !== null && owner === this._auth.getObjectId();
  });
  /** Identifies the start step for the caller's claiming policy. */
  isStartStep = computed(() => {
    const key = this.activeStep()?.stepKey;
    return !!key && (this._composed()?.graph.nodes.some(node => node.id === key && node.start === true) ?? false);
  });
  private _respondable = computed(
    () => this.isOpen() && this.activeStep() !== null && !this._composed()?.state.ownerOid && this.holdsStepRole(),
  );
  canClaim = computed(() => this._respondable() && this.isStartStep());
  /** An unowned later step is claimed for the viewer as part of their response, saving a separate claim. */
  claimsOnDecide = computed(() => this._respondable() && !this.isStartStep());
  canDecide = computed(() => (this.isOpen() && this.isOwner()) || this.claimsOnDecide());
  canRelease = computed(() => this.isOpen() && this.isOwner());
  /** The concrete roles the viewer holds in this case's class. */
  heldRoleNames = computed(() => this._roles().map(held => held.role));
  /** Candidates the agent proposed, for fields that reference one. */
  candidateOptions = computed(() =>
    (this._composed()?.dossier?.candidates ?? []).map(candidate => ({
      id: candidate.candidateId,
      label: typeof candidate.explanation['title'] === 'string' ? candidate.explanation['title'] : candidate.candidateId,
    })),
  );

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this._inFlight?.unsubscribe();
      this._loadFeedback?.clearError(this);
    });
  }

  load(caseId: string): void {
    this._inFlight?.unsubscribe();
    this._loadFeedback?.clearError(this);
    this._caseId.set(caseId);
    this._composed.set(null);
    this._form.set(null);
    this._roles.set([]);
    this._busy.set(false);
    this._loading.set(true);
    this._loadFailed.set(false);
    this._signInRedirect.set(false);
    this._caseMissing.set(false);
    this._loadStatus.set(null);
    this._commandError.set(null);
    this._lastLoadedForm = null;
    const read = this._read(caseId);
    this._inFlight = (this._loadFeedback?.track(read) ?? read).subscribe({
      next: () => {
        this._loading.set(false);
      },
      error: (error: unknown) => {
        // MSAL ends an intercepted request without a response when it starts an interactive redirect.
        if (error instanceof EmptyError && this._auth.redirecting()) {
          this._loading.set(false);
          this._signInRedirect.set(true);
          return;
        }
        this._loadFailed.set(true);
        this._caseMissing.set(error instanceof EngineCaseNotFoundError);
        this._loadStatus.set(httpStatusOf(error) ?? null);
        this._loading.set(false);
        this._trackLoadFailure(caseId, error, 'initial');
        if (!isExpectedReadRefusal(error)) {
          const message = error instanceof TimeoutError
            ? 'The workflow service did not respond in time. Retry or reopen the case.'
            : error instanceof HttpErrorResponse && error.status === 0
              ? 'Could not connect to the workflow service. Check your network connection and corporate sign-in, then retry or reopen the case.'
              : CASE_LOAD_FAILED_MESSAGE;
          if (this._loadFeedback) {
            this._loadFeedback.reportError(this, message);
          } else {
            this._toaster.showError(message);
          }
        }
      },
    });
  }

  /** Claims the start step for the viewer on their first interaction with it; repeated input while it is in flight is ignored. */
  claim(): void {
    const stepId = this.activeStep()?.stepId;
    if (!stepId || !this.canClaim()) {
      return;
    }
    this._command('claim', caseId => this._bounded(this._gateway.claimStep(caseId, stepId)), stepId, {
      event: TelemetryEvent.CaseClaim,
      properties: { outcome: 'claimed_on_interaction' },
    });
  }

  decide(outcome: string, values: Record<string, string>, reason = '', options: { claimStartStep?: boolean } = {}): void {
    const stepId = this.activeStep()?.stepId;
    const form = this._form();
    const claimStartStep = options.claimStartStep === true && this.canClaim();
    if (!stepId || !form || (!this.canDecide() && !claimStartStep) || this._busy()) {
      return;
    }
    if (outcomeNeedsReason(outcome, form) && !reason.trim()) {
      this._commandError.set('A reason is required for this outcome.');
      return;
    }
    const body = buildDecideBody(this.fields(), outcome, values, reason);
    this._command('decide', this._respond(stepId, caseId => this._gateway.decideStep(caseId, stepId, body), claimStartStep), stepId, decisionSuccess(outcome));
  }

  /** Sends a decision whose captures the screen built itself, such as a list of selected candidates the generic form cannot capture. */
  decideWith(body: DecideHttpRequest, options: { claimStartStep?: boolean } = {}): void {
    const stepId = this.activeStep()?.stepId;
    const claimStartStep = options.claimStartStep === true && this.canClaim();
    if (!stepId || (!this.canDecide() && !claimStartStep) || this._busy()) {
      return;
    }
    this._command(
      'decide',
      this._respond(stepId, caseId => this._gateway.decideStep(caseId, stepId, body), claimStartStep),
      stepId,
      decisionSuccess(body.outcome),
    );
  }

  release(reason: string): void {
    const stepId = this.activeStep()?.stepId;
    if (!stepId || !this.canRelease() || this._busy()) {
      return;
    }
    if (!reason.trim()) {
      this._commandError.set('A reason is required to release the step.');
      return;
    }
    this._command('release', caseId => this._bounded(this._gateway.releaseStep(caseId, stepId, { reason: reason.trim() })), stepId, {
      event: TelemetryEvent.CaseStepReleased,
    });
  }

  /** The owner accepts or rejects a Signal that arrived after the claim. */
  resolveSuggestion(signalKey: string, accept: boolean): void {
    this._command('resolve_suggestion', caseId => this._bounded(this._dossiers.resolveSuggestion(caseId, signalKey, accept)), undefined, {
      event: TelemetryEvent.CaseLateAttachResolved,
      properties: { outcome: accept ? 'accepted' : 'rejected' },
    });
  }

  /**
    * The decision as sent: on an unowned step the engine first records the viewer's claim, so the audit trail
   * still shows who owned the step when it was decided. A decision that then fails, answers nothing, or never answers
   * releases the step to its queue. Each request has its own deadline, so the release is never cut short by the decision's.
   */
  private _respond(
    stepId: string,
    decide: (caseId: string) => Observable<unknown>,
    claimStartStep = false,
  ): (caseId: string) => Observable<unknown> {
    if (!this.claimsOnDecide() && !claimStartStep) {
      return caseId => this._bounded(decide(caseId));
    }
    return caseId =>
      this._bounded(this._gateway.claimStep(caseId, stepId)).pipe(
        // The claim made on the viewer's behalf is still a claim, so it is reported like one.
        tap({
          next: () => this._trackClaim(caseId, stepId, 'claimed_on_respond'),
          error: (error: unknown) => this._trackClaim(caseId, stepId, claimFailureOutcome(error)),
        }),
        switchMap(() =>
          this._bounded(decide(caseId)).pipe(
            catchError((error: unknown) =>
              this._bounded(this._gateway.releaseStep(caseId, stepId, { reason: AUTO_RELEASE_REASON })).pipe(
                tap(() =>
                  this._telemetry.trackAppEvent(TelemetryEvent.CaseStepReleased, { engineCaseId: caseId, stepId, outcome: 'automatic' }),
                ),
                catchError(() => of(null)),
                switchMap(() => throwError(() => error)),
              ),
            ),
          ),
        ),
      );
  }

  private _trackClaim(engineCaseId: string, stepId: string, outcome: string): void {
    this._telemetry.trackAppEvent(TelemetryEvent.CaseClaim, { engineCaseId, stepId, outcome });
  }

  /**
   * One engine request that must end: a request that never answers, or that MSAL completes without a response, fails
   * so the buttons do not stay disabled until the page is reloaded.
   */
  private _bounded<T>(request: Observable<T>): Observable<T> {
    return request.pipe(throwIfEmpty(), timeout(ENGINE_COMMAND_TIMEOUT_MS));
  }

  private _command(
    command: CaseCommand,
    send: (caseId: string) => Observable<unknown>,
    stepId: string | undefined,
    success: CommandSuccess,
  ): void {
    const caseId = this._caseId();
    if (!caseId || this._busy()) {
      return;
    }
    this._busy.set(true);
    this._commandError.set(null);
    // Captured before sending: the re-read below can move the case to its next step.
    const context: TelemetryEventProperties = { engineCaseId: caseId, stepId };
    this._inFlight = send(caseId)
      .pipe(
        // Every request inside `send` is already bounded by `_bounded`.
        tap(() => this._telemetry.trackAppEvent(success.event, { ...context, ...success.properties })),
        map(() => null as string | null),
        catchError((error: unknown) => {
          this._telemetry.trackCommandFailed(command, error, context);
          if (command === 'claim') {
            this._telemetry.trackAppEvent(TelemetryEvent.CaseClaim, { ...context, outcome: claimFailureOutcome(error) });
          }
          return of(describeCommandError(error));
        }),
        // Re-read either way: after a rejection or conflict the screen must show what the engine now holds.
        switchMap(message => this._read(caseId).pipe(map(() => message))),
      )
      .subscribe({
        next: message => {
          this._commandError.set(message);
          this._busy.set(false);
          if (message) {
            this._toaster.showError(message);
          }
        },
        error: (error: unknown) => {
          const message = 'The command was sent but the case could not be reloaded.';
          this._commandError.set(message);
          this._busy.set(false);
          this._trackLoadFailure(caseId, error, 'after_command');
          this._toaster.showError(message);
        },
      });
  }

  private _trackLoadFailure(caseId: string, error: unknown, attempt: LoadAttempt): void {
    this._telemetry.trackAppEvent(TelemetryEvent.CaseLoadFailed, { engineCaseId: caseId, status: httpStatusOf(error), outcome: attempt });
  }

  /** Every read the screen renders from. The Dossier read has no deadline of its own, so the whole read is bounded. */
  private _read(caseId: string): Observable<ComposedCase> {
    return this._composition.compose(caseId).pipe(
      throwIfEmpty(),
      timeout(ENGINE_READ_TIMEOUT_MS),
      switchMap(composed => {
        const step = composed.state.activeStep;
        return forkJoin({
          composed: of(composed),
          roles: this._gateway.getMyRoles(composed.state.caseType),
          form: step ? this._gateway.getStepForm(caseId, step.stepId) : of(null),
        });
      }),
      tap(({ composed, roles, form }) => {
        // Unsubscribing on a switch already drops a stale read; this guards one that lands in the same tick.
        if (this._caseId() !== caseId) {
          return;
        }
        this._composed.set(composed);
        this._inbox.reportState(composed.state, composed.graph, composed.audit);
        this._roles.set(heldRoles(roles, composed.state.caseType));
        this._form.set(form);
        if (form) {
          this._trackFormLoaded(caseId, form);
        }
      }),
      map(({ composed }) => composed),
    );
  }

  private _trackFormLoaded(caseId: string, form: StepForm): void {
    const identity = `${caseId}|${form.stepId}|${form.classId}|${form.version}`;
    if (identity === this._lastLoadedForm) {
      return;
    }
    this._lastLoadedForm = identity;
    this._telemetry.trackAppEvent(TelemetryEvent.CaseStepFormLoaded, {
      engineCaseId: caseId,
      stepId: form.stepId,
      classId: form.classId,
      classVersion: form.version,
    });
  }
}
